"use client";

import { useEffect, useRef } from "react";

type FluidProfile = {
  base: [number, number, number];
  glow: [number, number, number];
  intensity: number;
  phase: number;
  drift: [number, number];
  scale: number;
  twist: number;
};

const profiles: FluidProfile[] = [
  { base: [0.008, 0.009, 0.011], glow: [0.84, 0.86, 0.89], intensity: 1.45, phase: 0.35, drift: [0.18, 0.09], scale: 1.14, twist: 0.32 },
  { base: [0.063, 0.063, 0.22], glow: [0.502, 0.69, 0.94], intensity: 1, phase: 0.35, drift: [-0.16, 0.13], scale: 1.28, twist: -0.24 },
  { base: [0.004, 0.022, 0.04], glow: [0, 0.698, 0.89], intensity: 1.16, phase: 0.35, drift: [0.14, -0.15], scale: 0.96, twist: 0.46 },
  { base: [0.008, 0.009, 0.011], glow: [1, 1, 1], intensity: 1.14, phase: 0.35, drift: [-0.12, 0.1], scale: 1.08, twist: -0.3 },
  // Personal Videography & Social Media: #44891A.
  { base: [0.008, 0.024, 0.006], glow: [0.267, 0.537, 0.102], intensity: 1.28, phase: 0.35, drift: [0.15, -0.11], scale: 1.18, twist: 0.28 },
];

const vertexShader = `attribute vec2 aPosition; void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }`;
const fragmentShader = `
  precision mediump float;
  uniform float uTime; uniform vec2 uResolution; uniform vec3 uBase; uniform vec3 uGlow;
  uniform float uIntensity; uniform float uPhase; uniform vec2 uDrift;
  uniform float uScale; uniform float uTwist;
  float random(vec2 point) { return fract(sin(dot(point, vec2(12.9898, 78.233))) * 43758.5453); }
  float wave(vec2 uv, float frequency, float speed, float time) { return sin(uv.x * frequency + time * speed) * cos(uv.y * frequency + time * speed); }
  void main() {
    float shortestSide = min(uResolution.x, uResolution.y);
    vec2 uv = (gl_FragCoord.xy * 2.0 - uResolution.xy) / shortestSide;
    float time = uTime * 0.325 + uPhase;
    uv *= uScale; uv.x += wave(uv + uPhase, 3.1, 0.42, time) * uDrift.x; uv.y += wave(uv + 10.0 + uPhase, 4.35, 0.32, time) * uDrift.y;
    float angle = atan(uv.y, uv.x); float radius = length(uv); angle += radius * uTwist; uv = vec2(cos(angle), sin(angle)) * radius;
    float d = -time * 0.78; float a = 0.0;
    for (int i = 0; i < 5; i++) { float iteration = float(i); a += cos(iteration - d - a * uv.x); d += sin(uv.y * iteration + a); }
    d += time * 0.78;
    float liquid = 1.0 - mix((sin(d) + 1.0) * 0.5, (cos(a) + 1.0) * 0.5, 0.64);
    float body = smoothstep(0.66, 0.92, liquid); float highlight = smoothstep(0.90, 0.99, liquid);
    vec3 color = mix(uBase, uGlow, body * 0.18 + highlight * 0.82) + (random(gl_FragCoord.xy) - 0.5) * 0.012;
    float alpha = min(0.9, (0.025 + body * 0.18 + highlight * 0.54) * uIntensity);
    gl_FragColor = vec4(color, alpha);
  }
`;

const clamp = (value: number) => Math.min(1, Math.max(0, value));
const mix = (from: number, to: number, amount: number) => from + (to - from) * amount;
const mixTuple = (from: [number, number, number] | [number, number], to: [number, number, number] | [number, number], amount: number) => from.map((value, index) => mix(value, to[index], amount)) as [number, number, number] & [number, number];

const settleNumber = (current: number, target: number, seconds: number, rate: number) => {
  const easedStep = (target - current) * (1 - Math.exp(-seconds / 1.15));
  const maximumStep = rate * seconds;
  return current + Math.min(maximumStep, Math.max(-maximumStep, easedStep));
};

function settleProfile(current: FluidProfile, target: FluidProfile, seconds: number): FluidProfile {
  const settleTuple = (from: number[], to: number[], rate: number) => from.map((value, index) => settleNumber(value, to[index], seconds, rate));
  return {
    base: settleTuple(current.base, target.base, 0.18) as [number, number, number],
    glow: settleTuple(current.glow, target.glow, 0.28) as [number, number, number],
    intensity: settleNumber(current.intensity, target.intensity, seconds, 0.36),
    phase: settleNumber(current.phase, target.phase, seconds, 0.7),
    drift: settleTuple(current.drift, target.drift, 0.13) as [number, number],
    scale: settleNumber(current.scale, target.scale, seconds, 0.15),
    twist: settleNumber(current.twist, target.twist, seconds, 0.16),
  };
}

function blendProfiles(from: FluidProfile, to: FluidProfile, amount: number): FluidProfile {
  return {
    base: mixTuple(from.base, to.base, amount) as [number, number, number],
    glow: mixTuple(from.glow, to.glow, amount) as [number, number, number],
    intensity: mix(from.intensity, to.intensity, amount), phase: mix(from.phase, to.phase, amount),
    drift: mixTuple(from.drift, to.drift, amount) as [number, number],
    scale: mix(from.scale, to.scale, amount), twist: mix(from.twist, to.twist, amount),
  };
}

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
  console.warn("Case-study fluid runway shader failed:", gl.getShaderInfoLog(shader));
  gl.deleteShader(shader); return null;
}

export function CaseStudyFluidRunway({ showHtc = false }: { showHtc?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const sequence = document.getElementById("case-study-sequence");
    const sectionIds = showHtc
      ? ["luma", "lightwheel", "vive", "varjo", "videography"]
      : ["luma", "lightwheel", "varjo", "videography"];
    const activeProfiles = showHtc ? profiles : profiles.filter((_, index) => index !== 2);
    const sections = sectionIds.map((id) => document.getElementById(id));
    if (!canvas || !sequence || sections.some((section) => !section)) return;
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false, powerPreference: "low-power" });
    if (!gl) return;
    const vertex = compile(gl, gl.VERTEX_SHADER, vertexShader);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, fragmentShader);
    if (!vertex || !fragment) return;
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    const position = gl.getAttribLocation(program, "aPosition");
    const uniforms = ["uTime", "uResolution", "uBase", "uGlow", "uIntensity", "uPhase", "uDrift", "uScale", "uTwist"].map((name) => gl.getUniformLocation(program, name));
    const buffer = gl.createBuffer();
    if (!buffer || position < 0 || uniforms.some((uniform) => !uniform)) return;
    const [timeUniform, resolutionUniform, baseUniform, glowUniform, intensityUniform, phaseUniform, driftUniform, scaleUniform, twistUniform] = uniforms as WebGLUniformLocation[];
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    const drawInterval = 72;
    let profile = profiles[0]; let targetProfile = profiles[0]; let lastDraw = 0; let simulationTime = 0; let timer: number | null = null; let updateFrame = 0; let active = false;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const resize = () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, window.innerWidth < 720 ? 1 : 1.25);
      const width = Math.max(1, Math.round(window.innerWidth * pixelRatio)); const height = Math.max(1, Math.round(window.innerHeight * pixelRatio));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    };
    const draw = () => {
      const time = simulationTime;
      const elapsedSeconds = Math.min(0.1, Math.max(0, time - lastDraw) / 1000);
      lastDraw = time;
      if (!reducedMotion) profile = settleProfile(profile, targetProfile, elapsedSeconds);
      gl.viewport(0, 0, canvas.width, canvas.height); gl.useProgram(program); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1f(timeUniform, time * 0.001); gl.uniform2f(resolutionUniform, canvas.width, canvas.height); gl.uniform3f(baseUniform, ...profile.base); gl.uniform3f(glowUniform, ...profile.glow); gl.uniform1f(intensityUniform, profile.intensity); gl.uniform1f(phaseUniform, profile.phase); gl.uniform2f(driftUniform, ...profile.drift); gl.uniform1f(scaleUniform, profile.scale); gl.uniform1f(twistUniform, profile.twist); gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const stop = () => { if (timer !== null) window.clearInterval(timer); timer = null; };
    const start = () => {
      if (reducedMotion || timer !== null || !active || document.visibilityState !== "visible") return;
      if (simulationTime === 0) simulationTime = performance.now();
      if (lastDraw === 0) lastDraw = simulationTime;
      draw();
      timer = window.setInterval(() => { simulationTime += drawInterval; draw(); }, drawInterval);
    };
    const update = () => {
      const sequenceRect = sequence.getBoundingClientRect(); const centers = sections.map((section) => (section!.getBoundingClientRect().top + window.scrollY) + section!.offsetHeight / 2);
      const viewportCenter = window.scrollY + window.innerHeight / 2;
      const easing = (amount: number) => amount * amount * (3 - 2 * amount);
      const segmentIndex = centers.findIndex((_, index) => index < centers.length - 1 && viewportCenter <= centers[index + 1]);
      const fromIndex = segmentIndex < 0 ? activeProfiles.length - 1 : segmentIndex;
      const toIndex = Math.min(fromIndex + 1, activeProfiles.length - 1);
      const segmentProgress = fromIndex === toIndex
        ? 0
        : clamp((viewportCenter - centers[fromIndex]) / Math.max(1, centers[toIndex] - centers[fromIndex]));
      targetProfile = blendProfiles(activeProfiles[fromIndex], activeProfiles[toIndex], easing(segmentProgress));
      if (reducedMotion) profile = targetProfile;
      // Home remains jet black so the portrait renderer's capture bounds never
      // show through. Begin the runway only after the Luma role heading reaches
      // the upper third of the viewport, then finish before its reel is centered.
      const lumaRect = sections[0]!.getBoundingClientRect();
      const lumaHeadingTop = document.getElementById("luma-title")?.getBoundingClientRect().top ?? lumaRect.top;
      const lumaRevealStart = window.innerHeight * 0.33;
      const lumaRevealEnd = window.innerHeight * 0.15;
      const entering = clamp((lumaRevealStart - lumaHeadingTop) / Math.max(1, lumaRevealStart - lumaRevealEnd)); const leaving = clamp(sequenceRect.bottom / 72);
      active = sequenceRect.bottom > 0 && lumaHeadingTop < lumaRevealStart;
      canvas.style.opacity = String(active ? Math.min(entering, leaving) : 0);
      if (reducedMotion && active) draw();
      if (active && document.visibilityState === "visible") start(); else stop();
    };
    const scheduleUpdate = () => {
      if (updateFrame !== 0) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = 0;
        update();
      });
    };
    const onResize = () => { resize(); scheduleUpdate(); };
    const onVisibilityChange = () => { if (document.visibilityState === "visible") update(); else stop(); };
    resize(); update(); window.addEventListener("scroll", scheduleUpdate, { passive: true }); window.addEventListener("resize", onResize, { passive: true }); document.addEventListener("visibilitychange", onVisibilityChange);
    return () => { stop(); if (updateFrame !== 0) window.cancelAnimationFrame(updateFrame); window.removeEventListener("scroll", scheduleUpdate); window.removeEventListener("resize", onResize); document.removeEventListener("visibilitychange", onVisibilityChange); gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); gl.getExtension("WEBGL_lose_context")?.loseContext(); };
  }, [showHtc]);

  return <canvas aria-hidden="true" className="case-study-fluid-runway" ref={canvasRef} />;
}
